// src/pages/HeatMap.tsx
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Popup, Polygon, CircleMarker } from 'react-leaflet';
import { Filter, Download, Layers, AlertTriangle, Activity, TrendingUp, Users, Eye } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { apiService, WardHeatMapDTO } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface CountyBoundary {
  id: number;
  name: string;
  coordinates: [number, number][][];
  fillColor: string;
}

export const HeatMap: React.FC = () => {
  const { user } = useAuth();
  const [activeLayer, setActiveLayer] = useState<'reports' | 'density' | 'urgency' | 'alerts'>('reports');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [wardData, setWardData] = useState<WardHeatMapDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAspirant, setIsAspirant] = useState(false);
  const [aspirantWard, setAspirantWard] = useState<WardHeatMapDTO | null>(null);

  // Nakuru County boundaries (simplified)
  const nakuruBoundary: CountyBoundary = {
    id: 32,
    name: 'Nakuru County',
    coordinates: [[
      [-0.45, 35.8], [-0.4, 35.9], [-0.35, 36.0], [-0.3, 36.1],
      [-0.25, 36.2], [-0.7, 36.4], [-0.8, 36.3], [-0.75, 36.1],
      [-0.65, 36.0], [-0.6, 35.9], [-0.5, 35.85], [-0.45, 35.8]
    ]],
    fillColor: '#0b2045'
  };

  useEffect(() => {
    fetchHeatMapData();
  }, []);

  const fetchHeatMapData = async () => {
    try {
      setIsLoading(true);
      const role = user?.role;
  
      if (role === 'ASPIRANT') {
        const response = await apiService.getMyWardHeatMap();
        if (response.data.success && response.data.data) {
          setAspirantWard(response.data.data);
          setWardData([response.data.data]);
        } else {
          toast.error('Could not load heat map for your ward');
        }
      } else if (role === 'ADMIN' || role === 'STAFF') {
        const response = await apiService.getHeatMapAll();
        if (response.data.success && response.data.data) {
          setWardData(response.data.data);
        } else {
          setWardData([]);
          toast.error('Failed to load heat map data');
        }
      }
    } catch (error: any) {
      console.error('Error fetching heat map data:', error);
      if (error.response?.status === 401) {
        toast.error('Your session may have expired. Please log in again.');
      } else {
        toast.error('Failed to load heat map data');
      }
      setWardData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    { id: 'water', name: 'Water Issues', color: 'bg-blue-500', icon: '💧' },
    { id: 'roads', name: 'Roads & Drainage', color: 'bg-orange-500', icon: '🛣️' },
    { id: 'health', name: 'Health Services', color: 'bg-green-500', icon: '🏥' },
    { id: 'security', name: 'Security', color: 'bg-red-500', icon: '👮' },
    { id: 'employment', name: 'Youth Employment', color: 'bg-purple-500', icon: '💼' },
    { id: 'education', name: 'Education', color: 'bg-indigo-500', icon: '📚' },
    { id: 'electricity', name: 'Electricity', color: 'bg-yellow-500', icon: '💡' },
  ];

  const getMarkerColor = (score: number, type: 'urgency' | 'density' | 'alerts' = 'urgency') => {
    switch (type) {
      case 'urgency':
        if (score >= 8) return 'bg-red-500 border-red-600';
        if (score >= 6) return 'bg-orange-500 border-orange-600';
        if (score >= 4) return 'bg-yellow-500 border-yellow-600';
        return 'bg-green-500 border-green-600';
      case 'density':
        if (score >= 200) return 'bg-purple-500 border-purple-600';
        if (score >= 100) return 'bg-indigo-500 border-indigo-600';
        if (score >= 50) return 'bg-blue-500 border-blue-600';
        return 'bg-teal-500 border-teal-600';
      case 'alerts':
        return 'bg-red-500 border-red-600';
      default:
        return 'bg-gray-500 border-gray-600';
    }
  };

  const getAlertIcon = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return '🔴';
      case 'HIGH':
        return '🟠';
      case 'MEDIUM':
        return '🟡';
      case 'LOW':
        return '🟢';
      default:
        return '⚪';
    }
  };

  const calculateRadius = (reportCount: number) => {
    const baseRadius = 10;
    const maxRadius = 30;
    const radius = Math.min(maxRadius, baseRadius + (reportCount / 20));
    return radius;
  };

  const handleExportData = () => {
    const dataToExport = isAspirant && aspirantWard ? [aspirantWard] : wardData;
    const csvContent = "data:text/csv;charset=utf-8," 
      + ["Ward Name,Report Count,Citizen Count,Urgency Score,Pending Reports,Resolved Reports,Top Issue,Alert Level,Increase %"]
          .concat(dataToExport.map(ward => 
            `"${ward.wardName}",${ward.reportCount},${ward.citizenCount},${ward.avgUrgencyScore.toFixed(1)},${ward.pendingReports},${ward.resolvedReports},"${ward.topIssueCategory}","${ward.alertPriority}",${ward.increasePercentage}`
          )).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `heatmap_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Heat map data exported successfully');
  };

  const filteredWards = wardData.filter(ward => {
    if (selectedCategory === 'all') return true;
    return ward.topIssueCategory?.toLowerCase() === selectedCategory.toLowerCase();
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0b2045] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading heat map data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0b2045]">Heat Map Analytics</h1>
          <p className="text-gray-600">
            {isAspirant && aspirantWard 
              ? `Viewing issues in your ward: ${aspirantWard.wardName}`
              : 'Visualize citizen reports and urgency across all wards'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
            <Layers size={16} className="text-gray-500" />
            <select
              className="border-0 bg-transparent text-sm focus:outline-none"
              value={activeLayer}
              onChange={(e) => setActiveLayer(e.target.value as any)}
            >
              <option value="reports">Report Density</option>
              <option value="urgency">Urgency Level</option>
              <option value="density">Citizen Density</option>
              <option value="alerts">Active Alerts</option>
            </select>
          </div>
          <button 
            onClick={handleExportData}
            className="inline-flex items-center px-4 py-2 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] transition-colors"
          >
            <Download size={16} className="mr-2" />
            Export Data
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-[#0b2045]/10">
          <div className="flex items-center">
            <div className="p-2 bg-[#0b2045]/10 rounded-lg mr-3">
              <Activity className="h-6 w-6 text-[#0b2045]" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Reports</p>
              <p className="text-xl font-bold text-[#0b2045]">
                {wardData.reduce((sum, ward) => sum + ward.reportCount, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#0b2045]/10">
          <div className="flex items-center">
            <div className="p-2 bg-red-50 rounded-lg mr-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Critical Alerts</p>
              <p className="text-xl font-bold text-[#0b2045]">
                {wardData.filter(w => w.alertPriority === 'CRITICAL').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#0b2045]/10">
          <div className="flex items-center">
            <div className="p-2 bg-[#3c8d2e]/10 rounded-lg mr-3">
              <Users className="h-6 w-6 text-[#3c8d2e]" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Covered Citizens</p>
              <p className="text-xl font-bold text-[#0b2045]">
                {wardData.reduce((sum, ward) => sum + ward.citizenCount, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#0b2045]/10">
          <div className="flex items-center">
            <div className="p-2 bg-[#0b2045]/10 rounded-lg mr-3">
              <TrendingUp className="h-6 w-6 text-[#0b2045]" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg. Urgency</p>
              <p className="text-xl font-bold text-[#0b2045]">
                {(wardData.reduce((sum, ward) => sum + ward.avgUrgencyScore, 0) / (wardData.length || 1)).toFixed(1)}/10
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Filters */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4 border border-[#0b2045]/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-[#0b2045]">Filter by Issue Category</h3>
            <Filter size={16} className="text-gray-400" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 ${
                selectedCategory === 'all'
                  ? 'bg-[#3c8d2e] text-white'
                  : 'bg-[#f4f7f6] text-[#0b2045] hover:bg-[#e8f1ec]'
              }`}
            >
              <Eye size={14} />
              <span>All Issues</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 ${
                  selectedCategory === cat.id
                    ? 'bg-[#0b2045]/10 text-[#0b2045] border border-[#0b2045]'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-[#f4f7f6]'
                }`}
              >
                <span className="text-lg">{cat.icon}</span>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Alerts */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-[#0b2045]/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-[#0b2045]">Active Alerts</h3>
            <AlertTriangle size={16} className="text-red-500" />
          </div>
          <div className="space-y-2">
            {wardData.filter(w => w.alertPriority === 'CRITICAL' || w.alertPriority === 'HIGH').slice(0, 3).map((ward) => (
              <div key={ward.wardId} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-red-800">{ward.wardName}</p>
                    <p className="text-sm text-red-600">
                      Urgency: {ward.avgUrgencyScore.toFixed(1)} | +{ward.increasePercentage}%
                    </p>
                  </div>
                  <span className="text-lg">{getAlertIcon(ward.alertPriority)}</span>
                </div>
              </div>
            ))}
            {wardData.filter(w => w.alertPriority === 'CRITICAL' || w.alertPriority === 'HIGH').length === 0 && (
              <p className="text-gray-500 text-sm">No active critical alerts</p>
            )}
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-[#0b2045]/10">
        <div className="h-[600px] relative">
          <MapContainer
            center={[-0.303, 36.071]}
            zoom={isAspirant ? 13 : 10}
            className="h-full w-full"
            scrollWheelZoom={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            {/* County Boundary */}
            <Polygon
              positions={nakuruBoundary.coordinates[0]}
              pathOptions={{
                fillColor: nakuruBoundary.fillColor,
                fillOpacity: 0.1,
                color: nakuruBoundary.fillColor,
                weight: 2,
                dashArray: '5, 5'
              }}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-bold text-[#0b2045]">{nakuruBoundary.name}</h3>
                  <p className="text-sm text-gray-600">County Boundary</p>
                </div>
              </Popup>
            </Polygon>
            
            {/* Ward Markers */}
            {filteredWards.map((ward) => {
              if (!ward.latitude || !ward.longitude) return null;
              
              const radius = calculateRadius(ward.reportCount);
              const colorClass = getMarkerColor(
                activeLayer === 'urgency' ? ward.avgUrgencyScore : 
                activeLayer === 'density' ? ward.citizenCount / 100 : 
                ward.reportCount,
                activeLayer as any
              );
              
              const fillColor = colorClass.split(' ')[0].replace('bg-', '');
              const borderColor = colorClass.includes('border-') ? 
                colorClass.split(' ')[1]?.replace('border-', '').replace('-600', '') : 'gray';
              
              return (
                <CircleMarker
                  key={ward.wardId}
                  center={[ward.latitude, ward.longitude]}
                  radius={radius}
                  pathOptions={{
                    fillColor: fillColor,
                    color: borderColor,
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0.6
                  }}
                >
                  <Popup>
                    <div className="p-2 min-w-[250px]">
                      <h3 className="font-bold text-[#0b2045] mb-2">{ward.wardName}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">Reports:</span>
                          <span>{ward.reportCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Citizens:</span>
                          <span>{ward.citizenCount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Urgency Score:</span>
                          <span className={`font-bold ${
                            ward.avgUrgencyScore >= 8 ? 'text-red-600' :
                            ward.avgUrgencyScore >= 6 ? 'text-orange-600' :
                            ward.avgUrgencyScore >= 4 ? 'text-yellow-600' : 'text-[#3c8d2e]'
                          }`}>
                            {ward.avgUrgencyScore.toFixed(1)}/10
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Pending:</span>
                          <span>{ward.pendingReports}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Resolved:</span>
                          <span>{ward.resolvedReports}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Top Issue:</span>
                          <span className="text-[#3c8d2e] font-medium">{ward.topIssueCategory}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Alert Level:</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            ward.alertPriority === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                            ward.alertPriority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                            ward.alertPriority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {ward.alertPriority}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Increase:</span>
                          <span className={`font-medium ${
                            ward.increasePercentage > 30 ? 'text-red-600' :
                            ward.increasePercentage > 15 ? 'text-orange-600' : 'text-[#3c8d2e]'
                          }`}>
                            {ward.increasePercentage > 0 ? `+${ward.increasePercentage}%` : `${ward.increasePercentage}%`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Updated: {new Date(ward.lastUpdated).toLocaleString()}
                        </div>
                      </div>
                      <button className="mt-3 w-full bg-[#0b2045] hover:bg-[#152c53] text-white text-sm font-medium py-2 px-3 rounded transition-colors">
                        View Detailed Reports
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
          
          {/* Map Legend */}
          <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 border border-[#0b2045]/10">
            <h4 className="font-medium text-[#0b2045] mb-2">Legend</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span className="text-sm">High Urgency (8-10)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                <span className="text-sm">Medium-High (6-7.9)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                <span className="text-sm">Medium (4-5.9)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-sm">Low (0-3.9)</span>
              </div>
              <div className="mt-2 pt-2 border-t border-[#0b2045]/10">
                <p className="text-xs text-gray-500">Circle size = Report volume</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-[#0b2045]/10">
        <div className="p-6 border-b border-[#0b2045]/10 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-[#0b2045]">Ward Analytics Dashboard</h3>
            <p className="text-sm text-gray-600">
              {isAspirant && aspirantWard 
                ? `Showing data for ${aspirantWard.wardName}` 
                : `Showing ${filteredWards.length} wards`}
            </p>
          </div>
          {!isAspirant && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              <select className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-[#3c8d2e] focus:border-transparent">
                <option>Report Count</option>
                <option>Urgency Score</option>
                <option>Citizen Count</option>
                <option>Increase %</option>
              </select>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#f4f7f6]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Ward
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Reports
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Citizens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Urgency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Resolution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Top Issue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Alert Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredWards.map((ward) => (
                <tr key={ward.wardId} className="hover:bg-[#f4f7f6] transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#0b2045]">{ward.wardName}</div>
                    <div className="text-xs text-gray-500">{ward.wardCode}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-[#0b2045]">{ward.reportCount}</div>
                    <div className="text-xs text-gray-500">
                      {ward.pendingReports} pending
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-[#0b2045]">{ward.citizenCount.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`w-24 h-2 rounded-full ${
                        ward.avgUrgencyScore >= 8 ? 'bg-red-500' :
                        ward.avgUrgencyScore >= 6 ? 'bg-orange-500' :
                        ward.avgUrgencyScore >= 4 ? 'bg-yellow-500' : 'bg-[#3c8d2e]'
                      }`}></div>
                      <span className="ml-2 text-sm font-bold">{ward.avgUrgencyScore.toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <span className="font-medium text-[#3c8d2e]">{ward.resolvedReports}</span>
                      <span className="text-gray-500"> / </span>
                      <span className="font-medium text-[#0b2045]">{ward.reportCount}</span>
                      <div className="text-xs text-gray-500">
                        {Math.round((ward.resolvedReports / ward.reportCount) * 100)}% resolved
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0b2045]/10 text-[#0b2045]">
                      {ward.topIssueCategory || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      ward.alertPriority === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                      ward.alertPriority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                      ward.alertPriority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-[#3c8d2e]/10 text-[#3c8d2e]'
                    }`}>
                      {getAlertIcon(ward.alertPriority)} {ward.alertPriority}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {ward.increasePercentage !== 0 ? (
                      <div className="flex items-center">
                        <TrendingUp className={`h-4 w-4 mr-1 ${
                          ward.increasePercentage > 30 ? 'text-red-500' :
                          ward.increasePercentage > 15 ? 'text-orange-500' : 'text-[#3c8d2e]'
                        }`} />
                        <span className={`font-medium ${
                          ward.increasePercentage > 30 ? 'text-red-600' :
                          ward.increasePercentage > 15 ? 'text-orange-600' : 'text-[#3c8d2e]'
                        }`}>
                          {ward.increasePercentage > 0 ? `+${ward.increasePercentage}%` : `${ward.increasePercentage}%`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">Stable</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!isAspirant && (
          <div className="p-4 border-t border-[#0b2045]/10 text-center">
            <button 
              onClick={() => toast.success('Loading more data...')}
              className="text-[#3c8d2e] hover:text-[#2d7022] font-medium transition-colors"
            >
              Load More Wards →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeatMap;